import { useState, useRef, useEffect } from "react";
import { Send, Loader2, Swords, Flag, Star } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

const SCENARIOS = [
  "They came home past curfew and won't say where they were",
  "They're refusing to put the phone down at dinner",
  "Their grades dropped and they shut down when I ask",
  "They snapped at me and slammed their door",
];

export default function Simulator({ child }) {
  const [scenario, setScenario] = useState("");
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, report]);

  const start = async (s) => {
    const sc = (s ?? scenario).trim();
    if (!sc) { toast.error("Describe the situation to practise"); return; }
    setLoading(true); setReport(null);
    try {
      const { data } = await api.post("/simulate/start", { scenario: sc, child_id: child?.id || null });
      setSession(data.id); setMessages(data.messages);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "parent", text }]);
    setLoading(true);
    try {
      const { data } = await api.post(`/simulate/${session}/reply`, { text });
      setMessages(data.messages);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const end = async () => {
    setLoading(true);
    try { const { data } = await api.post(`/simulate/${session}/end`); setReport(data.report); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const reset = () => { setSession(null); setMessages([]); setReport(null); setScenario(""); };

  if (!session) {
    return (
      <div className="dk-raised rounded-2xl p-6 border space-y-4" data-testid="simulator-setup">
        <div className="flex items-center gap-2"><Swords size={18} style={{ color: "var(--accent)" }} /><h2 className="font-serif text-[20px] font-medium">Practise a hard conversation</h2></div>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>You'll chat with an AI playing your child, so you can rehearse calmly. Nothing here is real — it's a safe space to try.</p>
        <textarea className="dk-input resize-none" rows={2} value={scenario} maxLength={400} placeholder="Describe the situation you want to practise…" onChange={(e) => setScenario(e.target.value)} data-testid="simulator-scenario-input" />
        <div className="flex flex-wrap gap-2">
          {SCENARIOS.map((s) => <button key={s} className="badge-soft hover:opacity-80 text-left" onClick={() => { setScenario(s); start(s); }} data-testid="simulator-preset">{s}</button>)}
        </div>
        <div className="flex justify-end">
          <button className="btn btn-primary" disabled={loading} onClick={() => start()} data-testid="simulator-start-btn">{loading ? <Loader2 size={16} className="animate-spin" /> : <Swords size={16} />} Start practising</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="simulator-chat">
      <div className="dk-card p-4 space-y-3 max-h-[420px] overflow-y-auto">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "parent" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-[15px]`} style={m.role === "parent" ? { background: "var(--sage)", color: "#fff" } : { background: "var(--card-alt)", color: "var(--text)" }} data-testid={`sim-msg-${m.role}`}>
              <span className="block text-[10px] uppercase tracking-wide opacity-70 mb-0.5">{m.role === "parent" ? "You" : "Your child (AI)"}</span>
              {m.text}
            </div>
          </div>
        ))}
        {loading && <div className="flex justify-start"><div className="rounded-2xl px-4 py-2" style={{ background: "var(--card-alt)" }}><Loader2 size={16} className="animate-spin" /></div></div>}
        <div ref={endRef} />
      </div>

      {report ? (
        <div className="dk-card p-6 space-y-4 anim-in" data-testid="simulator-report">
          <div className="flex items-center gap-2"><Star style={{ color: "var(--accent)" }} /><h3 className="font-serif text-[20px] font-medium">How that went</h3>
            <span className="badge-soft ml-auto">Warmth {report.warmth_score}/5</span></div>
          <p className="text-sm">{report.summary}</p>
          {report.went_well?.length > 0 && <div><p className="text-xs uppercase tracking-wide mb-1" style={{ color: "var(--success)" }}>What went well</p><ul className="text-sm list-disc pl-5 space-y-1">{report.went_well.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
          {report.try_next?.length > 0 && <div><p className="text-xs uppercase tracking-wide mb-1" style={{ color: "var(--warning)" }}>Try next time</p><ul className="text-sm list-disc pl-5 space-y-1">{report.try_next.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
          <button className="btn btn-outline" onClick={reset} data-testid="simulator-new-btn">Practise another</button>
        </div>
      ) : (
        <div className="flex gap-2">
          <input className="dk-input" value={input} placeholder="Type what you'd say…" onKeyDown={(e) => e.key === "Enter" && send()} onChange={(e) => setInput(e.target.value)} data-testid="simulator-input" />
          <button className="btn btn-primary px-4" disabled={loading} onClick={send} data-testid="simulator-send-btn"><Send size={16} /></button>
          <button className="btn btn-outline px-4" disabled={loading} onClick={end} data-testid="simulator-end-btn"><Flag size={16} /> End</button>
        </div>
      )}
    </div>
  );
}
